import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { addJobSchedule, checkDuplicateSchedules } from '../services/scheduleService';
import { searchFranchisees } from '../services/franchiseeService';
import { searchClients as searchSuppliers, getClientById } from '../services/clientService';
import { searchHouseholds as searchAppClients, getClientByHouseholdId } from '../services/householdService';
import { listServiceTypes } from '../services/serviceTypeService';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { FaMapMarkerAlt, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { terminology, allocationTypes, scheduleTypes, frequencyOptions, frequencyIntervalUnits, quoteStatuses, allocationOptions, allocationIntervalUnits, formatters } from '../config/terminology';
import ConfirmationModal from '../components/ConfirmationModal';
import { isCarwashService } from '../utils/carwashUtils';

const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false); // Flag to prevent blur during selection

    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            let results = data.franchisees || data.clients || data.households || [];
            
            // Filter out inactive households if this is a household search
            if (data.households) {
                results = results.filter(household => household.status === 'active');
            }
            
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return {
        searchTerm, setSearchTerm,
        suggestions, setSuggestions,
        isSearching,
        showSuggestions, setShowSuggestions,
        isSelecting, setIsSelecting
    };
};

const AddJobSchedulePage = () => {
    const { register, handleSubmit, watch, setValue, resetField, formState: { errors, isSubmitting, isSubmitted } } = useForm({
        mode: 'onBlur',        defaultValues: {
            fseId: '',
            fseNameSearch: '',
            clientId: '',
            clientNameSearch: '',
            householdId: '',
            householdNameSearch: '',
            scheduleType: '',
            startDate: '',
            endDate: '',
            occurrences: '',
            jobTime: '',
            jobDate: '',
            frequencyOption: '',
            frequencyIntervalValue: '',
            frequencyIntervalUnit: 'Days',
            allotedHours: '',
            pricePerHour: '',
            additionalPrice: '',
            allocationType: 'fixed-fee',
            allocationOption: 'interval',
            allocationIntervalValue: 7,
            allocationIntervalUnit: 'Days',
            invoicePricePerHour: '',
            description: '',
            adminNotes: '',
            notesForFranchisee: '',
            quoteStatus: 'approved',
            ndisJob: false,
        }
    });

    const navigate = useNavigate();
    const location = useLocation();
    const [apiError, setApiError] = useState(null);
    const [serviceSelectionError, setServiceSelectionError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [formDataForConfirmation, setFormDataForConfirmation] = useState(null);
    const [selectedAppClient, setSelectedAppClient] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [duplicateWarning, setDuplicateWarning] = useState(null); // { hasDuplicates, duplicates } from /schedule/check-duplicates, or null if not checked/failed
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);

    const franchiseeSearch = useSearch(searchFranchisees);
    const supplierSearchOptions = useMemo(() => ({}), []);
    const supplierSearch = useSearch(searchSuppliers, supplierSearchOptions);
    const selectedSupplierId = watch('clientId');
    // Pass clientId directly as a primitive, not as an object
    const appClientSearch = useSearch(searchAppClients, { clientId: selectedSupplierId });
    const scheduleType = watch('scheduleType');
    const frequencyOption = watch('frequencyOption');
    const frequencyIntervalValue = watch('frequencyIntervalValue');
    const frequencyIntervalUnit = watch('frequencyIntervalUnit');
    const additionalPrice = watch('additionalPrice');
    const allocationType = watch('allocationType');
    const allocationOption = watch('allocationOption');
    const quoteStatus = watch('quoteStatus');
    const selectedAppClientId = watch('householdId');
    const ndisJob = watch('ndisJob');
    const invoicePricePerHour = watch('invoicePricePerHour');
    const pricePerHour = watch('pricePerHour');

    useEffect(() => {
        const fetchServiceTypes = async () => {
            try {
                const services = await listServiceTypes();
                setAllServiceTypes(services);
            } catch (error) {
                console.error("Failed to fetch service types:", error);
                setApiError("Could not load service types.");
            } finally {
                setServiceTypesLoading(false);
            }
        };
        fetchServiceTypes();
    }, []);

    // Fetch supplier data when supplier is selected
    useEffect(() => {
        const fetchSupplierData = async () => {
            if (selectedSupplierId) {
                try {
                    const supplierData = await getClientById(selectedSupplierId);
                    setSelectedSupplier(supplierData);
                } catch (error) {
                    console.error("Failed to fetch supplier data:", error);
                    setSelectedSupplier(null);
                }
            } else {
                setSelectedSupplier(null);
            }
        };
        fetchSupplierData();
    }, [selectedSupplierId]);

    // Auto-update duration (description) and invoice prices when FSE price changes and supplier has hourly rate (aged care only)
    useEffect(() => {
        console.log('[AddJobSchedule] Auto-calc useEffect fired:', {
            pricePerHour,
            agedCare: selectedSupplier?.agedCare,
            hourlyRate: selectedSupplier?.hourlyRate,
            durationInIncrements: selectedSupplier?.durationInIncrements,
            allocationType: watch('allocationType'),
        });
        if (pricePerHour && selectedSupplier?.agedCare && selectedSupplier?.hourlyRate) {
            const fsePrice = parseFloat(pricePerHour);
            const hourlyRate = parseFloat(selectedSupplier.hourlyRate);

            if (!isNaN(fsePrice) && !isNaN(hourlyRate) && hourlyRate > 0) {
                // Use the raw FSE price for duration calculation
                let duration = fsePrice / hourlyRate;

                // Round up to 15-minute increments (0.25 hours) if durationInIncrements is enabled
                if (selectedSupplier?.durationInIncrements) {
                    duration = Math.ceil(duration * 4) / 4;
                }

                // Update description with calculated duration
                const descriptionText = `Duration ${duration.toFixed(2)} hours based on hourly rate of $${hourlyRate.toFixed(2)} + GST.`;
                setValue('description', descriptionText, { shouldValidate: false });

                // Calculate new Invoice Price (ex. GST) based on the calculated duration
                const invoicePriceExGST = duration * hourlyRate;
                console.log('[AddJobSchedule] Auto-calc result:', { duration, invoicePriceExGST, willOverwriteAdditionalPrice: invoicePriceExGST.toFixed(2) });
                setValue('additionalPrice', invoicePriceExGST.toFixed(2), { shouldValidate: true });

                // **NEW: Save the duration directly into allotedHours so it maps to the invoice quantity later**
                setValue('allotedHours', duration, { shouldValidate: true });
            }
        }
    }, [pricePerHour, selectedSupplier, ndisJob, setValue]);

    useEffect(() => {
        if (additionalPrice && !isNaN(parseFloat(additionalPrice)) && parseFloat(additionalPrice) >= 0) {
            const invoicePriceExGST = parseFloat(additionalPrice);
            // Don't add GST for NDIS jobs
            const invoicePriceWithGST = ndisJob
                ? invoicePriceExGST.toFixed(2)
                : (invoicePriceExGST * 1.1).toFixed(2);
            setValue('invoicePricePerHour', invoicePriceWithGST, { shouldValidate: true });
        } else {
            setValue('invoicePricePerHour', '', { shouldValidate: false });
        }
    }, [additionalPrice, ndisJob, setValue]);

    // Sync allocation fields with frequency fields for Fixed Hours allocation type (only for recurring schedules)
    useEffect(() => {
        if (allocationType === 'fixed-hours' && scheduleType === 'recurring') {
            setValue('allocationOption', frequencyOption, { shouldValidate: false });
            setValue('allocationIntervalValue', frequencyIntervalValue, { shouldValidate: false });
            setValue('allocationIntervalUnit', frequencyIntervalUnit, { shouldValidate: false });
        }
    }, [allocationType, scheduleType, frequencyOption, frequencyIntervalValue, frequencyIntervalUnit, setValue]);

    // Reset description when allocation type changes
    useEffect(() => {
        setValue('description', '', { shouldValidate: false });
    }, [allocationType, setValue]);

    const createSearchHandlers = (fieldNamePrefix, searchState, idField, nameField) => ({
        onChange: (e) => {
            const value = e.target.value;
            searchState.setSearchTerm(value);
            setValue(fieldNamePrefix + 'NameSearch', value);
            setValue(idField, '');
            if (fieldNamePrefix === 'client') {
                setValue('householdId', '');
                setValue('householdNameSearch', '');
                setSelectedAppClient(null);
                appClientSearch.setSearchTerm('');
                appClientSearch.setSuggestions([]);
            } else if (fieldNamePrefix === 'household') {
                setSelectedAppClient(null);
            }
            if (value.length < 3) searchState.setShowSuggestions(false);
        },
        onSelect: async (item) => {
            searchState.setIsSelecting(true); // Set flag to prevent blur
            searchState.setShowSuggestions(false);
            searchState.setSuggestions([]);

            setValue(idField, item.id);
            let displayName;

            if (fieldNamePrefix === 'household') {
                displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                setSelectedAppClient(item);

                // If no supplier is selected, automatically fetch and populate client info
                if (!watch('clientId')) {
                    try {
                        const clientData = await getClientByHouseholdId(item.id);
                        if (clientData) {
                            setValue('clientId', clientData.id);
                            setValue('clientNameSearch', clientData.clientName || '');
                        }
                    } catch (error) {
                        console.error('Failed to fetch client for household:', error);
                    }
                }
                // Clear search term and suggestions to prevent dropdown from reappearing
                searchState.setSearchTerm('');
                searchState.setSuggestions([]);
            } else if (fieldNamePrefix === 'fse') {
                const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            } else {
                displayName = item.clientName || '';
                setSelectedAppClient(null);
            }

            setValue(fieldNamePrefix + 'NameSearch', displayName);

            // Reset the flag after a brief delay
            setTimeout(() => {
                searchState.setIsSelecting(false);
            }, 100);
        },
        renderSuggestions: (item) => {
            if (fieldNamePrefix === 'fse') {
                const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            } else if (fieldNamePrefix === 'household') {
                return `${item.firstName || ''} ${item.lastName || ''}`.trim() + (item.addressLine1 ? ` (${item.addressLine1})` : '');
            } else {
                return item.clientName || '';
            }
        }
    });

    const fseHandlers = createSearchHandlers('fse', franchiseeSearch, 'fseId', 'franchiseName');
    const supplierHandlers = createSearchHandlers('client', supplierSearch, 'clientId', 'clientName');
    const appClientHandlers = createSearchHandlers('household', appClientSearch, 'householdId', 'householdName');

    const handleServiceToggle = (serviceId) => {
        setSelectedServiceIds(prev => {
            const isCurrentlySelected = prev.includes(serviceId);
            const isCarwash = isCarwashService(serviceId, allServiceTypes);

            // If deselecting the service
            if (isCurrentlySelected) {
                return prev.filter(id => id !== serviceId);
            }

            // If selecting a carwash service
            if (isCarwash) {
                // Clear all other selections and only select carwash
                return [serviceId];
            }

            // If selecting a non-carwash service
            // Check if any currently selected services are carwash
            const hasCarwashSelected = prev.some(id => isCarwashService(id, allServiceTypes));

            if (hasCarwashSelected) {
                // Replace carwash selection with this new service
                return [serviceId];
            } else {
                // Normal multi-selection for non-carwash services
                return [...prev, serviceId];
            }
        });
        setServiceSelectionError(null);
    };

    const onSubmit = async (data) => {
        setApiError(null);
        setServiceSelectionError(null);

        // 🔍 LOG: Form field values when "Schedule Job" button is pressed
        console.log('📝 FORM SUBMISSION - Raw form data:', {
            allocationType: data.allocationType,
            allocationOption: data.allocationOption,
            allocationIntervalValue: data.allocationIntervalValue,
            allocationIntervalUnit: data.allocationIntervalUnit,
            frequencyOption: data.frequencyOption,
            frequencyIntervalValue: data.frequencyIntervalValue,
            frequencyIntervalUnit: data.frequencyIntervalUnit,
            scheduleType: data.scheduleType,
            allFormData: data
        });

        if (selectedServiceIds.length === 0) {
            setServiceSelectionError("Please select at least one service.");
            return;
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        console.log('Constructed servicesArray:', servicesArray); // Log servicesArray immediately after creation

        if (servicesArray.length !== selectedServiceIds.length) {
             setServiceSelectionError("Could not find names for all selected services. Please try again.");
             console.error("Failed to map service IDs to names:", selectedServiceIds, serviceNamesMap);
             return;
        }        let submitData = {
            ...data,
            services: servicesArray, // Array of { serviceTypeId, name }
            allotedHours: data.allotedHours ? parseFloat(data.allotedHours) : null,
            pricePerHour: data.pricePerHour ? parseFloat(data.pricePerHour) : null,
            additionalPrice: data.additionalPrice ? parseFloat(data.additionalPrice) : null,
            invoicePricePerHour: data.invoicePricePerHour ? parseFloat(data.invoicePricePerHour) : null,
            jobTime: data.jobTime || null,
            description: data.description || null,
            adminNotes: data.adminNotes || null,
            notesForFranchisee: data.notesForFranchisee || null,
            quoteStatus: data.quoteStatus || 'approved', // Pass quoteStatus from form, default to 'approved'
            ndisJob: data.ndisJob || false, // Include NDIS Job flag
        };

        // DEBUG: Log NDIS job field tracking
        console.log('🔍 ADD JOB SCHEDULE - NDIS DEBUGGING:');
        console.log('  Form data.ndisJob:', data.ndisJob);
        console.log('  Submit data.ndisJob:', submitData.ndisJob);
        console.log('  Full submitData before cleanup:', JSON.stringify(submitData, null, 2));

        if (submitData.allotedHours === null) delete submitData.allotedHours;
        if (submitData.pricePerHour === null) delete submitData.pricePerHour;
        if (submitData.additionalPrice === null) delete submitData.additionalPrice;
        if (submitData.invoicePricePerHour === null) delete submitData.invoicePricePerHour;
        if (submitData.jobTime === null) delete submitData.jobTime;
        if (submitData.description === null) delete submitData.description;
        if (submitData.adminNotes === null) delete submitData.adminNotes;
        if (submitData.notesForFranchisee === null) delete submitData.notesForFranchisee;

        if (submitData.scheduleType === 'one-off') {
            submitData.jobDate = submitData.startDate;
        } else {
            delete submitData.jobDate;
        }

        if (submitData.scheduleType === 'recurring') {
            if (submitData.frequencyOption === 'monthly') {
                submitData.frequencyType = 'Monthly';
                delete submitData.frequencyValue;
            } else if (submitData.frequencyOption === 'interval') {
                submitData.frequencyType = submitData.frequencyIntervalUnit;
                submitData.frequencyValue = parseInt(submitData.frequencyIntervalValue, 10);
            } else {
                // No frequency selected - don't send frequency fields at all
                delete submitData.frequencyType;
                delete submitData.frequencyValue;
            }
        }

        // Transform allocation fields for backend
        if (submitData.allocationType === 'fixed-fee') {
            // Keep as 'fixed-fee', no allocationValue needed
            delete submitData.allocationValue;
        } else if (submitData.allocationType === 'fixed-hours') {
            if (submitData.allocationOption === 'monthly') {
                submitData.allocationType = 'monthly';
                delete submitData.allocationValue;
            } else {
                submitData.allocationType = submitData.allocationIntervalUnit.toLowerCase(); // 'days' or 'weeks'
                submitData.allocationValue = parseInt(submitData.allocationIntervalValue, 10);
            }
        }

        delete submitData.frequencyOption;
        delete submitData.frequencyIntervalValue;
        delete submitData.frequencyIntervalUnit;
        
        // Delete allocation UI-specific fields after transformation
        delete submitData.allocationOption;
        delete submitData.allocationIntervalValue;
        delete submitData.allocationIntervalUnit;        delete submitData.fseNameSearch;
        delete submitData.clientNameSearch;
        delete submitData.householdNameSearch;

        // DEBUG: Final data being sent to API
        console.log('  Final submitData sent to API:', JSON.stringify(submitData, null, 2));

        try {
            await addJobSchedule(submitData);
            
            // Preserve filters when navigating back after successful add
            const navigationState = { message: 'Job schedule added successfully!' };
            if (location.state?.filters) {
                navigationState.filters = location.state.filters;
            }
            
            navigate('/job-scheduler', { state: navigationState });
        } catch (error) {
            setApiError(error.message || 'Failed to add job schedule. Please check details.');
            console.error("Submission error:", error);
        }
    };

    const formatAddress = (appClient) => {
        if (!appClient) return '';
        const parts = [
            appClient.addressLine1,
            appClient.addressLine2,
            appClient.state,
            appClient.postalCode,
        ];
        return parts.filter(Boolean).join(', ');
    };

    // Frequency text for the in-progress form (frequencyOption/frequencyIntervalValue/Unit fields).
    // Shared between buildJobSummary and the duplicate-warning comparison.
    const getFrequencyText = (data) => {
        if (data.scheduleType !== 'recurring') return null;
        if (data.frequencyOption === 'monthly') return 'Monthly';
        if (data.frequencyOption === 'interval' && data.frequencyIntervalValue && data.frequencyIntervalUnit) {
            return `Every ${data.frequencyIntervalValue} ${data.frequencyIntervalUnit.toLowerCase()}`;
        }
        return null;
    };

    // Formats a Firestore Timestamp-shaped value ({_seconds, _nanoseconds}) from an existing schedule doc.
    const formatDuplicateDate = (timestamp) => {
        if (!timestamp) return null;
        try {
            const date = timestamp._seconds != null ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
            if (isNaN(date.getTime())) return null;
            return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (e) {
            return null;
        }
    };

    // Renders the duplicate-schedule warning to be composed alongside buildJobSummary inside ConfirmationModal.
    const renderDuplicateWarning = (data) => {
        if (!duplicateWarning?.hasDuplicates || !duplicateWarning.duplicates?.length) return null;

        const newServiceNames = selectedServiceIds
            .map(id => allServiceTypes.find(s => s.id === id)?.name)
            .filter(Boolean);
        const newAddress = formatAddress(selectedAppClient);
        const newFrequency = getFrequencyText(data);

        // Franchisee/client/address are identical across every match by definition (they are the
        // matching criteria), so they are stated once here rather than repeated per duplicate.
        const count = duplicateWarning.duplicates.length;
        const overlappingIds = new Set(selectedServiceIds);
        const newDateLabel = data.scheduleType === 'one-off' ? 'Date' : 'Start Date';

        const Field = ({ label, children }) => (
            <div><span className="font-medium">{label}:</span> {children}</div>
        );

        return (
            <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-4 text-left">
                <div className="flex items-start mb-1 text-amber-800">
                    <FaExclamationTriangle className="mr-2 mt-1 flex-shrink-0" />
                    <span className="font-semibold">
                        {count} possible duplicate schedule{count > 1 ? 's' : ''} found
                    </span>
                </div>
                <p className="text-sm text-amber-800 mb-3">
                    This franchisee and {terminology.household.singular.toLowerCase()} already have {count > 1 ? 'schedules' : 'a schedule'} sharing at least one selected service. You can still create this schedule.
                </p>

                <div className="bg-white/70 rounded border border-amber-200 px-3 py-2 mb-3 text-sm text-gray-700 space-y-1">
                    <Field label="Franchisee">{data.fseNameSearch || 'N/A'}</Field>
                    <Field label={terminology.household.singular}>{data.householdNameSearch || 'N/A'}</Field>
                    {newAddress && <Field label="Address">{newAddress}</Field>}
                </div>

                <div className="bg-white rounded border border-gray-200 p-3 mb-3">
                    <h5 className="text-xs font-semibold uppercase text-gray-500 mb-2">New schedule (being created)</h5>
                    <div className="text-sm text-gray-700 space-y-1">
                        <Field label="Services">{newServiceNames.join(', ') || 'N/A'}</Field>
                        <Field label="Schedule Type">{formatters.formatScheduleType(data.scheduleType) || 'N/A'}</Field>
                        {newFrequency && <Field label="Frequency">{newFrequency}</Field>}
                        {data.startDate && <Field label={newDateLabel}>{data.startDate}</Field>}
                        {data.endDate && <Field label="End Date">{data.endDate}</Field>}
                    </div>
                </div>

                <h5 className="text-xs font-semibold uppercase text-amber-700 mb-2">
                    Existing schedule{count > 1 ? 's' : ''} ({count})
                </h5>
                {/* Inner scroll keeps the list from burying the job summary below the warning. */}
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                    {duplicateWarning.duplicates.map((dup) => {
                        const existingDateLabel = dup.scheduleType === 'one-off' ? 'Date' : 'Start Date';
                        const existingDate = formatDuplicateDate(dup.scheduleType === 'one-off' ? dup.jobDate : dup.startDate);
                        const existingEndDate = formatDuplicateDate(dup.endDate);
                        const frequencyText = dup.scheduleType === 'recurring' && dup.frequencyType
                            ? formatters.formatFrequency(dup.frequencyType, dup.frequencyValue)
                            : null;
                        const price = dup.additionalPrice != null
                            ? `$${Number(dup.additionalPrice).toFixed(2)} (ex. GST)`
                            : dup.pricePerHour != null
                                ? `$${Number(dup.pricePerHour).toFixed(2)}/hr`
                                : null;

                        return (
                            <div key={dup.id} className="bg-white rounded border border-amber-200 p-3">
                                <div className="text-sm text-gray-700 space-y-1">
                                    <Field label="Services">
                                        {(dup.services || []).length > 0
                                            ? (dup.services || []).map((s, i) => (
                                                <span key={s.serviceTypeId || i}>
                                                    {i > 0 && ', '}
                                                    {/* Emphasise the services that caused the match. */}
                                                    <span className={overlappingIds.has(s.serviceTypeId) ? 'font-semibold text-amber-800' : ''}>
                                                        {s.name}
                                                    </span>
                                                </span>
                                            ))
                                            : 'N/A'}
                                    </Field>
                                    <Field label="Schedule Type">{formatters.formatScheduleType(dup.scheduleType) || 'N/A'}</Field>
                                    {frequencyText && <Field label="Frequency">{frequencyText}</Field>}
                                    {existingDate && <Field label={existingDateLabel}>{existingDate}</Field>}
                                    {existingEndDate && <Field label="End Date">{existingEndDate}</Field>}
                                    {price && <Field label="Price">{price}</Field>}
                                    <Field label="Status">{formatters.formatQuoteStatus(dup.quoteStatus) || dup.status || 'N/A'}</Field>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Helper to build a formatted summary for the modal
    const buildJobSummary = (data) => {
        const isQuoteRequest = data.quoteStatus === 'pending-quote';

        // Helper to check if a value is a valid number
        const isValidNumber = (value) => {
            return value !== '' && value !== null && value !== undefined && !isNaN(parseFloat(value)) && isFinite(value);
        };

        return (
            <div className="space-y-4 text-left">
                {/* Basic Information */}
                <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Basic Information</h4>
                    <div className="space-y-1 text-sm">
                        {data.fseNameSearch && <div><span className="font-medium">Franchisee:</span> {data.fseNameSearch}</div>}
                        {data.clientNameSearch && <div><span className="font-medium">{terminology.client.singular}:</span> {data.clientNameSearch}</div>}
                        {data.householdNameSearch && <div><span className="font-medium">{terminology.household.singular}:</span> {data.householdNameSearch}</div>}
                    </div>
                </div>

                {/* Schedule Details */}
                {(data.scheduleType || data.startDate || data.endDate || data.jobTime || getFrequencyText(data)) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Schedule Details</h4>
                        <div className="space-y-1 text-sm">
                            {data.scheduleType && <div><span className="font-medium">Schedule Type:</span> {data.scheduleType}</div>}
                            {getFrequencyText(data) && <div><span className="font-medium">Frequency:</span> {getFrequencyText(data)}</div>}
                            {data.startDate && <div><span className="font-medium">{data.scheduleType === 'one-off' ? 'Date' : 'Start Date'}:</span> {data.startDate}</div>}
                            {data.endDate && <div><span className="font-medium">End Date:</span> {data.endDate}</div>}
                            {data.occurrences && isValidNumber(data.occurrences) && <div><span className="font-medium">Occurrences:</span> {data.occurrences}</div>}
                            {data.jobTime && <div><span className="font-medium">Job Time:</span> {data.jobTime}</div>}
                        </div>
                    </div>
                )}

                {/* Services */}
                {selectedServiceIds.length > 0 && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Selected Services</h4>
                        <div className="flex flex-wrap gap-1 text-sm">
                            {selectedServiceIds.map(serviceId => {
                                const service = allServiceTypes?.find(s => s.id === serviceId);
                                return service ? (
                                    <span key={serviceId} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                        {service.name}
                                    </span>
                                ) : null;
                            })}
                        </div>
                    </div>
                )}

                {/* Pricing & Allocation */}
                {!isQuoteRequest && (data.allocationType || isValidNumber(data.allotedHours) || isValidNumber(data.pricePerHour) || isValidNumber(data.additionalPrice) || isValidNumber(data.invoicePricePerHour)) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Pricing & Allocation</h4>
                        <div className="space-y-1 text-sm">
                            {data.allocationType && <div><span className="font-medium">Allocation Type:</span> {data.allocationType}</div>}
                            {isValidNumber(data.allotedHours) && <div><span className="font-medium">Allotted Hours:</span> {data.allotedHours}</div>}
                            {isValidNumber(data.pricePerHour) && <div><span className="font-medium">FSE Price:</span> ${parseFloat(data.pricePerHour).toFixed(2)}</div>}
                            {isValidNumber(data.additionalPrice) && <div><span className="font-medium">Invoice Price (ex. GST):</span> ${parseFloat(data.additionalPrice).toFixed(2)}</div>}
                            {isValidNumber(data.invoicePricePerHour) && <div><span className="font-medium">Invoice Price:</span> ${parseFloat(data.invoicePricePerHour).toFixed(2)}</div>}
                        </div>
                    </div>
                )}

                {/* Notes */}
                {(data.description || data.adminNotes || data.notesForFranchisee) && (
                    <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Additional Notes</h4>
                        <div className="space-y-2 text-sm">
                            {data.description && (
                                <div>
                                    <span className="font-medium">Description (Invoice notes):</span>
                                    <div className="bg-gray-50 p-2 rounded mt-1 text-gray-700">{data.description}</div>
                                </div>
                            )}
                            {data.adminNotes && (
                                <div>
                                    <span className="font-medium">Admin Notes:</span>
                                    <div className="bg-gray-50 p-2 rounded mt-1 text-gray-700">{data.adminNotes}</div>
                                </div>
                            )}
                            {data.notesForFranchisee && (
                                <div>
                                    <span className="font-medium">Notes for Franchisee:</span>
                                    <div className="bg-gray-50 p-2 rounded mt-1 text-gray-700">{data.notesForFranchisee}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Intercept submit to check for duplicate schedules, then show the confirmation modal.
    // The duplicate check is advisory only - it must never block the modal from opening.
    const handleFormSubmit = async (data) => {
        setFormDataForConfirmation(data);
        setDuplicateWarning(null);
        setCheckingDuplicates(true);
        try {
            const result = await checkDuplicateSchedules({
                fseId: data.fseId,
                householdId: data.householdId,
                serviceTypeIds: selectedServiceIds,
            });
            setDuplicateWarning(result);
        } catch (error) {
            console.error('Duplicate schedule check failed; proceeding without a warning:', error);
            setDuplicateWarning(null);
        } finally {
            setCheckingDuplicates(false);
            setShowConfirmModal(true);
        }
    };

    // Only submit after confirmation
    const handleConfirm = async () => {
        setShowConfirmModal(false);
        if (formDataForConfirmation) {
            await onSubmit(formDataForConfirmation);
            setFormDataForConfirmation(null);
        }
    };

    return (
        <div className="max-w-4xl mx-auto pt-8 pb-20">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-jims-cyan-dark">Schedule New Job(s)</h1>
                    <Link 
                        to="/job-scheduler" 
                        state={location.state?.filters ? { filters: location.state.filters } : {}}
                        className="text-jims-cyan-dark hover:text-jims-cyan underline"
                    >
                        Back to Scheduler
                    </Link>
                </div>

                {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

                {/* --- JOB APPROVAL PILLS --- */}
                <div className="col-span-1 md:col-span-4 mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Approval</label>
                    <input type="hidden" {...register("quoteStatus", { required: "Please select job approval status." })} />
                    <div className="flex flex-wrap gap-2">
                        {[
                            { value: 'approved',          label: 'This job is approved' },
                            { value: 'pending-quote',     label: 'This job requires quotation' },
                            { value: 'pending-approval',  label: 'This job is pending approval from supplier' },
                        ].map(({ value, label }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setValue('quoteStatus', value, { shouldValidate: true })}
                                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-1 ${
                                    quoteStatus === value
                                        ? 'bg-jims-cyan text-white border-jims-cyan'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-jims-cyan hover:text-jims-cyan'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {errors.quoteStatus && <p className="input-error">{errors.quoteStatus.message}</p>}
                </div>
                {/* --- END JOB APPROVAL PILLS --- */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative">
                        <label htmlFor="fseNameSearch" className="block text-sm font-medium text-gray-700">Franchisee *</label>
                        <input id="fseNameSearch" type="text" autoComplete="off"
                            {...register("fseNameSearch", { required: "Franchisee search is required." })}
                            value={watch('fseNameSearch')}
                            onChange={fseHandlers.onChange}
                            onBlur={() => franchiseeSearch.setShowSuggestions(false)}
                            onFocus={() => {
                                if (franchiseeSearch.suggestions.length > 0 && !watch('fseId')) {
                                    franchiseeSearch.setShowSuggestions(true);
                                }
                            }}
                            className={`mt-1 block w-full input-field ${errors.fseNameSearch || (errors.fseId && !watch('fseId')) ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <input type="hidden" {...register("fseId", { required: "Please select a franchisee." })} />
                        {errors.fseId && !watch('fseId') && <p className="input-error">{errors.fseId.message}</p>}
                        {franchiseeSearch.showSuggestions && (
                            <ul className="suggestions-list">
                                {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                                 franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                                    <li key={item.id} className="suggestion-item" 
                                        onMouseDown={() => {
                                            fseHandlers.onSelect(item);
                                            franchiseeSearch.setShowSuggestions(false);
                                            franchiseeSearch.setSearchTerm('');
                                        }}>
                                        {fseHandlers.renderSuggestions(item)}
                                    </li>
                                )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
                            </ul>
                        )}
                    </div>

                    <div className="relative">
                        <label htmlFor="clientNameSearch" className="block text-sm font-medium text-gray-700">{terminology.client.singular} *</label>
                        <input id="clientNameSearch" type="text" autoComplete="off"
                            {...register("clientNameSearch", { required: `${terminology.client.singular} search is required.` })}
                            value={watch('clientNameSearch')}
                            onChange={supplierHandlers.onChange}
                            onBlur={() => supplierSearch.setShowSuggestions(false)}
                            onFocus={() => {
                                if (supplierSearch.suggestions.length > 0 && !watch('clientId')) {
                                    supplierSearch.setShowSuggestions(true);
                                }
                            }}
                            className={`mt-1 block w-full input-field ${errors.clientNameSearch || (errors.clientId && !watch('clientId')) ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <input type="hidden" {...register("clientId", { required: `Please select a ${terminology.client.singular.toLowerCase()}.` })} />
                        {errors.clientId && !watch('clientId') && <p className="input-error">{errors.clientId.message}</p>}
                        {supplierSearch.showSuggestions && (
                             <ul className="suggestions-list">
                                {supplierSearch.isSearching ? <li className="searching">Searching...</li> :
                                 supplierSearch.suggestions.length > 0 ? supplierSearch.suggestions.map(item => (
                                    <li key={item.id} className="suggestion-item" 
                                        onMouseDown={() => {
                                            supplierHandlers.onSelect(item);
                                            supplierSearch.setShowSuggestions(false);
                                            supplierSearch.setSearchTerm('');
                                        }}>
                                        {supplierHandlers.renderSuggestions(item)}
                                    </li>
                                )) : supplierSearch.searchTerm.length >= 3 && <li className="no-results">No {terminology.client.plural.toLowerCase()} found.</li>}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="relative">
                    <label htmlFor="householdNameSearch" className="block text-sm mt-4 font-medium text-gray-700">{terminology.household.singular} *</label>
                    <input id="householdNameSearch" type="text" autoComplete="off"
                        {...register("householdNameSearch", { required: `${terminology.household.singular} search is required.` })}
                        onChange={appClientHandlers.onChange}
                        onBlur={() => appClientSearch.setShowSuggestions(false)}
                        onFocus={() => {
                            if (appClientSearch.suggestions.length > 0 && !watch('householdId')) {
                                appClientSearch.setShowSuggestions(true);
                            }
                        }}
                        className={`mt-1 block w-full input-field ${errors.householdNameSearch || (errors.householdId && !watch('householdId')) ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Type to search..."
                    />
                    <input type="hidden" {...register("householdId", { required: `Please select a ${terminology.household.singular.toLowerCase()}.` })} />
                    {errors.householdId && !watch('householdId') && <p className="input-error">{errors.householdId.message}</p>}
                    {appClientSearch.showSuggestions && appClientSearch.searchTerm && !appClientSearch.isSelecting && (
                         <ul className="suggestions-list">
                            {appClientSearch.isSearching ? <li className="searching">Searching...</li> :
                             appClientSearch.suggestions.length > 0 ? appClientSearch.suggestions.map(item => (
                                <li key={item.id} className="suggestion-item" 
                                    onMouseDown={() => {
                                        appClientHandlers.onSelect(item);
                                        appClientSearch.setShowSuggestions(false);
                                    }}>
                                    {`${item.firstName || ''} ${item.lastName || ''}`.trim()} ({item.addressLine1 || 'No Address'})
                                </li>
                            )) : appClientSearch.searchTerm.length >= 3 && !appClientSearch.isSearching && <li className="no-results">No {terminology.household.plural.toLowerCase()} found{selectedSupplierId ? ` for this ${terminology.client.singular.toLowerCase()}` : ''}.</li>}
                        </ul>
                    )}
                    {selectedAppClientId && selectedAppClient && (
                        <>
                            <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                                <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                                {formatAddress(selectedAppClient)}
                            </div>
                            {(selectedAppClient.siteContact?.name || selectedAppClient.siteContact?.email || selectedAppClient.siteContact?.phone) && (
                                <div className="mt-2 bg-gray-100 rounded-md px-4 py-3 flex flex-wrap gap-6 items-center">
                                    {selectedAppClient.siteContact?.name && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Case Manager</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.name}</span>
                                        </div>
                                    )}
                                    {selectedAppClient.siteContact?.email && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Email</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.email}</span>
                                        </div>
                                    )}
                                    {selectedAppClient.siteContact?.phone && (
                                        <div className="min-w-[160px] flex-1">
                                            <span className="block text-xs font-semibold text-gray-600">Phone</span>
                                            <span className="block text-sm text-gray-800">{selectedAppClient.siteContact.phone}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div>
                    <label className="block text-sm mt-4 font-medium text-gray-700 mb-2">Services *</label>
                    {serviceTypesLoading ? (
                        <p className="text-gray-500">Loading services...</p>
                    ) : allServiceTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {allServiceTypes.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                const isCarwash = isCarwashService(service.id, allServiceTypes);
                                const hasCarwashSelected = selectedServiceIds.some(id => isCarwashService(id, allServiceTypes));
                                const hasNonCarwashSelected = selectedServiceIds.some(id => !isCarwashService(id, allServiceTypes));

                                // Determine if this service should be disabled
                                const isDisabled = (isCarwash && hasNonCarwashSelected) || (!isCarwash && hasCarwashSelected);

                                return (
                                    <button
                                        key={service.id}
                                        type="button"
                                        onClick={() => !isDisabled && handleServiceToggle(service.id)}
                                        disabled={isDisabled}
                                        className={`
                                            px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-150
                                            flex items-center justify-center
                                            ${isSelected
                                                ? 'bg-jims-cyan text-white border-jims-cyan'
                                                : isDisabled
                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                                            }
                                        `}
                                        title={isDisabled
                                            ? (isCarwash
                                                ? 'Cannot select carwash services with other services'
                                                : 'Cannot select other services when carwash is selected')
                                            : ''
                                        }
                                    >
                                        {isSelected && <FaCheck className="mr-1 text-xs" />}
                                        {service.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500">No services available.</p>
                    )}
                    {serviceSelectionError && <p className="input-error mt-2">{serviceSelectionError}</p>}
                </div>

                {/* --- Schedule Type/Start Date/Job Time section (with dynamic required) --- */}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end mt-4">
                     <div>
                        <label htmlFor="scheduleType" className="block text-sm font-medium text-gray-700">Schedule Type{quoteStatus === 'pending-quote' ? '' : ' *'}</label>
                        <select id="scheduleType"
                            {...register("scheduleType", { required: quoteStatus === 'pending-quote' ? false : "Schedule Type is required." })}
                            className={`mt-1 block w-full input-field ${errors.scheduleType ? 'border-red-500' : 'border-gray-300'}`}
                        >
                            <option value="">Select Type...</option>
                            {scheduleTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                        {errors.scheduleType && <p className="input-error">{errors.scheduleType.message}</p>}
                    </div>
                     <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                            {scheduleType === 'one-off' ? 'Date' : 'Start Date'}
                        </label>
                        <input id="startDate" type="date"
                            {...register("startDate")}
                            className={`mt-1 block w-full input-field ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {errors.startDate && <p className="input-error">{errors.startDate.message}</p>}
                    </div>
                     <div>
                        <label htmlFor="jobTime" className="block text-sm font-medium text-gray-700">Job Time (Optional)</label>
                        <input id="jobTime" type="time"
                            {...register("jobTime")}
                            className={`mt-1 block w-full input-field ${errors.jobTime ? 'border-red-500' : 'border-gray-300'}`}
                        />
                    </div>
                </div>

                {/* --- Recurring Details section (no heading, radio buttons side by side) --- */}
                {scheduleType === 'recurring' && (
                    <div className="mt-6">
                        {/* <h3 className="text-lg font-medium text-gray-800 mb-3">Job Frequency</h3> */}
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                Job Frequency (Optional)
                            </label>
                        <div className="flex items-center gap-8 mb-4">
                            <label className="flex items-center">
                                <input
                                    id="frequencyOptionInterval"
                                    type="radio"
                                    value="interval"
                                    {...register("frequencyOption")}
                                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                />
                                <span className="ml-2">Every</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    {...register("frequencyIntervalValue", {
                                        required: frequencyOption === 'interval' ? "Value is required." : false,
                                        valueAsNumber: frequencyOption === 'interval' ? true : false,
                                        min: frequencyOption === 'interval' ? { value: 1, message: "Must be at least 1." } : undefined,
                                        validate: value => {
                                            if (frequencyOption !== 'interval') return true;
                                            if (frequencyOption === 'interval' && !Number.isInteger(value)) {
                                                return "Must be a whole number.";
                                            }
                                            return true;
                                        }
                                    })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`w-16 input-field text-sm ml-2 ${errors.frequencyIntervalValue ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                <select
                                    {...register("frequencyIntervalUnit", { required: frequencyOption === 'interval' ? "Unit is required." : false })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`input-field text-sm ml-2 ${errors.frequencyIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    {frequencyIntervalUnits.map(unit => (
                                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex items-center ml-6">
                                <input
                                    id="frequencyOptionMonthly"
                                    type="radio"
                                    value="monthly"
                                    {...register("frequencyOption")}
                                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                />
                                <span className="ml-2">Monthly</span>
                            </label>
                        </div>
                        {errors.frequencyIntervalValue && frequencyOption === 'interval' && <p className="input-error ml-7">{errors.frequencyIntervalValue.message}</p>}
                        {errors.frequencyIntervalUnit && frequencyOption === 'interval' && <p className="input-error ml-7">{errors.frequencyIntervalUnit.message}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date (Optional)</label>
                                <input id="endDate" type="date"
                                    {...register("endDate")}
                                    className={`mt-1 block w-full input-field border-gray-300`}
                                />
                            </div>
                            <div>
                                <label htmlFor="occurrences" className="block text-sm font-medium text-gray-700">No. of Occurrences (Optional)</label>
                                <input id="occurrences" type="number" min="1"
                                    {...register("occurrences", { min: { value: 1, message: "Must be at least 1." } })}
                                    className={`mt-1 block w-full input-field ${errors.occurrences ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="e.g., 10"
                                />
                                {errors.occurrences && <p className="input-error">{errors.occurrences.message}</p>}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Allocation Type section (with dynamic required) --- */}
                {/* <div className="pt-8 mt-8 border-t border-gray-200"> */}
                    <div className="space-y-6 mt-6">
                        {/* Allocation Type Pills */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Allocation Type{quoteStatus === 'pending-quote' ? '' : ' *'}
                            </label>
                            <div className="flex space-x-4">
                                {allocationTypes.map(type => (
                                    <label key={type.value} className="inline-flex items-center">
                                        <input
                                            type="radio"
                                            value={type.value}
                                            {...register("allocationType", { required: quoteStatus === 'pending-quote' ? false : "Please select an allocation type." })}
                                            className="sr-only"
                                        />
                                        <div className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${
                                            allocationType === type.value 
                                                ? 'border-jims-cyan bg-jims-cyan text-white' 
                                                : 'border-gray-300 bg-white text-gray-700 hover:border-jims-cyan'
                                        }`}>
                                            {type.label}
                                        </div>
                                    </label>
                                ))}                            </div>
                            {errors.allocationType && <p className="input-error mt-2">{errors.allocationType.message}</p>}
                        </div>

                        {/* NDIS Job Checkbox */}
                        <div>
                            <label className="inline-flex items-center">
                                <input
                                    type="checkbox"
                                    {...register("ndisJob")}
                                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">NDIS Job</span>
                            </label>
                        </div>

                        {/* Conditional Fields Based on Allocation Type */}
                        {allocationType && (
                            <div className="space-y-6">
                                {/* For Set Fee: Show FSE Price, Invoice Price (ex. GST), and Invoice Price */}
                                {allocationType === 'fixed-fee' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">                                        <div>
                                            <label htmlFor="pricePerHour" className="block text-sm font-medium text-gray-700">
                                                FSE Price ({ndisJob ? 'GST Free' : 'w/ GST'}){quoteStatus === 'approved' ? ' *' : ''}
                                            </label>
                                            <input id="pricePerHour" type="number" step="0.01" min="0"
                                                {...register("pricePerHour", {
                                                    required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.pricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 55.00"
                                            />
                                            {errors.pricePerHour && <p className="input-error">{errors.pricePerHour.message}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="additionalPrice" className="block text-sm font-medium text-gray-700">
                                                Invoice Price (ex. GST)
                                            </label>
                                            <input id="additionalPrice" type="number" step="0.01" min="0"
                                                {...register("additionalPrice", {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.additionalPrice ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 77.00"
                                            />
                                            {errors.additionalPrice && <p className="input-error">{errors.additionalPrice.message}</p>}
                                        </div>                                        <div>
                                            <label htmlFor="invoicePricePerHour" className="block text-sm font-medium text-gray-700">
                                                Invoice Price ({ndisJob ? 'GST Free' : 'w/ GST'})
                                            </label>
                                            <input id="invoicePricePerHour" type="number" step="0.01" min="0"
                                                {...register("invoicePricePerHour", {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.invoicePricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 60.50"
                                            />
                                            {errors.invoicePricePerHour && <p className="input-error">{errors.invoicePricePerHour.message}</p>}
                                        </div>
                                    </div>
                                )}

                                {/* For Fixed Hours: Show allocation frequency options and fields */}
                                {allocationType === 'fixed-hours' && (
                                    <div className="space-y-6">
                                        {/* Allocation Frequency Options - Only show if schedule is recurring */}
                                        {scheduleType === 'recurring' && (
                                            <fieldset className="space-y-4">
                                                <legend className="block text-sm font-medium text-gray-700 mb-0">Allocation Frequency *</legend>
                                                <p className="italic text-sm text-gray-600 mt-1 mb-1">Allocation is important for locking in the hours alloted for the job. The franchisee cannot exceed the allocated hours for the particular frequency selected</p>
                                                <div className="flex items-center gap-8">
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            id="allocationOptionInterval"
                                                            type="radio"
                                                            value="interval"
                                                            {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                            className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                                        />
                                                        <label htmlFor="allocationOptionInterval" className="flex items-center space-x-2 text-sm text-gray-700">
                                                            <span>Every</span>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                {...register("allocationIntervalValue", {
                                                                    required: allocationOption === 'interval' && "Value is required.",
                                                                    valueAsNumber: true,
                                                                    min: allocationOption === 'interval' ? { value: 1, message: "Must be at least 1." } : undefined,
                                                                    validate: value => allocationOption !== 'interval' || Number.isInteger(value) || "Must be a whole number."
                                                                })}
                                                                disabled={allocationOption !== 'interval'}
                                                                className={`w-16 input-field text-sm ${errors.allocationIntervalValue ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                            />
                                                            <select
                                                                {...register("allocationIntervalUnit", { required: allocationOption === 'interval' && "Unit is required." })}
                                                                disabled={allocationOption !== 'interval'}
                                                                className={`input-field text-sm ${errors.allocationIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                            >
                                                                {allocationIntervalUnits.map(unit => (
                                                                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            id="allocationOptionMonthly"
                                                            type="radio"
                                                            value="monthly"
                                                            {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                            className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                                        />
                                                        <label htmlFor="allocationOptionMonthly" className="text-sm text-gray-700">Monthly</label>
                                                    </div>
                                                </div>
                                                {errors.allocationIntervalValue && allocationOption === 'interval' && <p className="input-error ml-7">{errors.allocationIntervalValue.message}</p>}
                                                {errors.allocationIntervalUnit && allocationOption === 'interval' && <p className="input-error ml-7">{errors.allocationIntervalUnit.message}</p>}
                                                {errors.allocationOption && <p className="input-error">{errors.allocationOption.message}</p>}
                                            </fieldset>
                                        )}

                                        {/* Fixed Hours, Invoice Price (ex. GST), FSE Price, Invoice Price */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                            <div>
                                                <label htmlFor="allotedHours" className="block text-sm font-medium text-gray-700">
                                                    Fixed Allocated Hours
                                                </label>
                                                <input id="allotedHours" type="number" step="0.01" min="0"
                                                    {...register("allotedHours", {
                                                        valueAsNumber: true,
                                                        min: { value: 0, message: "Cannot be negative." }
                                                    })}
                                                    className={`mt-1 block w-full input-field ${errors.allotedHours ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="e.g., 1.5"
                                                />
                                                {errors.allotedHours && <p className="input-error">{errors.allotedHours.message}</p>}
                                            </div>                                            <div>
                                                <label htmlFor="pricePerHour" className="block text-sm font-medium text-gray-700">
                                                    FSE price/hour ({ndisJob ? 'GST Free' : 'w/ GST'}){quoteStatus === 'approved' ? ' *' : ''}
                                                </label>
                                                <input id="pricePerHour" type="number" step="0.01" min="0"
                                                    {...register("pricePerHour", {
                                                        required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                        valueAsNumber: true,
                                                        min: { value: 0, message: "Cannot be negative." }
                                                    })}
                                                    className={`mt-1 block w-full input-field ${errors.pricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="e.g., 55.00"
                                                />
                                                {errors.pricePerHour && <p className="input-error">{errors.pricePerHour.message}</p>}
                                            </div>
                                            <div>
                                                <label htmlFor="additionalPrice" className="block text-sm font-medium text-gray-700">
                                                    Invoice Price (ex. GST)
                                                </label>
                                                <input id="additionalPrice" type="number" step="0.01" min="0"
                                                    {...register("additionalPrice", {
                                                        valueAsNumber: true,
                                                        min: { value: 0, message: "Cannot be negative." }
                                                    })}
                                                    className={`mt-1 block w-full input-field ${errors.additionalPrice ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="e.g., 77.00"
                                                />
                                                {errors.additionalPrice && <p className="input-error">{errors.additionalPrice.message}</p>}
                                            </div>
                                            <div>                                                <label htmlFor="invoicePricePerHour" className="block text-sm font-medium text-gray-700">
                                                    Invoice Price ({ndisJob ? 'GST Free' : 'w/ GST'})
                                                </label>
                                                <input id="invoicePricePerHour" type="number" step="0.01" min="0"
                                                    {...register("invoicePricePerHour", {
                                                        valueAsNumber: true,
                                                        min: { value: 0, message: "Cannot be negative." }
                                                    })}
                                                    className={`mt-1 block w-full input-field ${errors.invoicePricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="e.g., 60.50"
                                                />
                                                {errors.invoicePricePerHour && <p className="input-error">{errors.invoicePricePerHour.message}</p>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                {/* </div> */}

                <div className="mt-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Invoice notes)</label>
                    <textarea id="description"
                        {...register("description")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter notes that will appear on the client invoice..."
                    />
                    {errors.description && <p className="input-error">{errors.description.message}</p>}
                </div>
                <div className="mt-4">
                    <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700">Admin notes</label>
                    <textarea id="adminNotes"
                        {...register("adminNotes")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.adminNotes ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Internal notes for admin use only..."
                    />
                    {errors.adminNotes && <p className="input-error">{errors.adminNotes.message}</p>}
                </div>
                <div>
                    <label htmlFor="notesForFranchisee" className="block text-sm mt-4 font-medium text-gray-700">Notes for franchisee</label>
                    <textarea id="notesForFranchisee"
                        {...register("notesForFranchisee")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.notesForFranchisee ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter any specific instructions or notes for the franchisee..."
                    />
                    {errors.notesForFranchisee && <p className="input-error">{errors.notesForFranchisee.message}</p>}
                </div>

                {/* Sticky Action Bar
                  Variant A (swap): full viewport width
                        {/* <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)] flex justify-end gap-3 px-6 py-3">
                          ... buttons...
                           </div/
                  Variant B (active): replace this div with:
                  */}
                  <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                    <div className="container mx-auto px-6 py-3 max-w-4xl flex justify-end gap-3">
                       <Link
                        to="/job-scheduler"
                        state={location.state?.filters ? { filters: location.state.filters } : {}}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </Link>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting || checkingDuplicates || franchiseeSearch.isSearching || supplierSearch.isSearching || appClientSearch.isSearching}>
                        {checkingDuplicates ? 'Checking for duplicates...' : isSubmitting ? (quoteStatus === 'pending-quote' ? 'Sending...' : 'Scheduling...') : (quoteStatus === 'pending-quote' ? 'Send Request for Quote' : 'Schedule Job(s)')}
                    </button>
                    </div>
                  </div>

                {isSubmitted && (Object.keys(errors).length > 0 || serviceSelectionError || apiError) && (
                    <div className="mt-4 text-center text-red-600 text-sm">
                        <FaExclamationTriangle className="inline mr-1" /> Please correct the errors in the form above.
                    </div>
                )}

                {/* "Add job anyway" only reads correctly as an answer to a duplicate warning; without
                    one there is nothing to overrule, so it stays "Confirm" (ticket 72). */}
                <ConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleConfirm}
                    title={formDataForConfirmation?.quoteStatus === 'pending-quote' ? 'Confirm Quote Request' : 'Confirm Job Scheduling'}
                    message={formDataForConfirmation ? (
                        <>
                            {renderDuplicateWarning(formDataForConfirmation)}
                            {buildJobSummary(formDataForConfirmation)}
                        </>
                    ) : 'No job details entered.'}
                    confirmText={duplicateWarning?.hasDuplicates ? 'Add job anyway' : 'Confirm'}
                    wide={!!duplicateWarning?.hasDuplicates}
                />

                <style jsx>{`
                    .input-field {
                        padding: 0.5rem 0.75rem;
                        border-width: 1px;
                        border-radius: 0.375rem;
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        outline: none;
                        transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                    }
                    .input-field:focus {
                        border-color: #06B6D4;
                        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.3);
                    }
                    .input-error {
                        margin-top: 0.25rem;
                        font-size: 0.75rem;
                        color: #EF4444;
                    }
                    .suggestions-list {
                        position: absolute;
                        z-index: 10;
                        width: 100%;
                        background-color: white;
                        border: 1px solid #D1D5DB;
                        border-radius: 0.375rem;
                        margin-top: 0.25rem;
                        max-height: 15rem;
                        overflow-y: auto;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    }
                    .suggestion-item {
                        padding: 0.5rem 0.75rem;
                        cursor: pointer;
                    }
                    .suggestion-item:hover {
                        background-color: #67E8F9;
                    }
                    .searching, .no-results {
                         padding: 0.5rem 0.75rem;
                         color: #6B7280;
                    }
                    .btn {
                        padding: 0.5rem 1rem;
                        font-weight: 700;
                        border-radius: 0.375rem;
                        transition: background-color 0.3s ease;
                        border: 1px solid transparent;
                    }
                    .btn-primary {
                        background-color: #06B6D4;
                        color: white;
                    }
                    .btn-primary:hover {
                        background-color: #0E7490;
                    }
                    .btn-primary:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    .btn-secondary {
                        background-color: #E5E7EB;
                        color: #1F2937;
                    }
                    .btn-secondary:hover {
                         background-color: #D1D5DB;
                    }
                `}</style>
            </form>
        </div>
    );
};

export default AddJobSchedulePage;
